import { InvalidStateError } from '../errors/internal-error';
import { valOrThrow } from './common';
import { isDefined, isEmpty, isEmptyObject, isObject } from './guards';
import { hashString } from './string';

/**
 * Returns a random choice from the given array
 * @param arr Array of items to choose from
 * @returns Random choice from the given array
 */
export const randomChoice = <T>(arr: T[]): T => {
	const chosen = arr[Math.floor(Math.random() * arr.length)];
	return valOrThrow(chosen, 'No value returned');
};

/**
 * Returns uniq array of values
 * @param array Array of values
 * @param by Function to get the unique key
 * @returns Array of unique values
 */
export const uniq = <TArray>(array: TArray[], by?: (item: TArray) => any): TArray[] => {
	if (!isDefined(by)) {
		return Array.from(new Set(array));
	}

	const unique = new Map();
	array.forEach((item) => {
		const key = by(item);
		if (!unique.has(key)) unique.set(key, item);
	});

	return Array.from(unique.values());
};

/**
 * Based on the input string returns a fixed element from the input array
 * @param from Array of elements
 * @param str Input string
 * @returns Fixed element from the input array
 */
export const getFixedFrom = <T>(from: Array<T> | ReadonlyArray<T>, str: string): T => {
	if (from.length === 0) throw new InvalidStateError('Input array is empty');
	const item = from[hashString(str) % from.length];
	if (!isDefined(item)) return from[0]!;
	return item;
};

/**
 * Excludes values from the given array.
 * @param array Array of values
 * @param values Values to exclude
 * @returns Array of values without the excluded values
 */
export const exclude = <T>(array: Array<T> | ReadonlyArray<T>, ...values: T[]): T[] => array.filter((item) => !values.includes(item));

/**
 * Finds an element in the array and transforms it using the given function.
 * @param array Array of values
 * @param findTransform Function to find and transform the element
 * @returns Transformed element or undefined if not found
 */
export const findAndTransform = <T, R>(array: T[], findTransform: (item: T) => R | undefined): R | undefined =>
	array.reduce<R | undefined>((acc, item) => {
		if (isDefined(acc)) return acc;
		return findTransform(item);
	}, undefined);

/**
 * Strict version of findAndTransform function, which throws an error if the value is not found.
 * @param array Array of values
 * @param findTransform Function to find and transform the element
 * @returns Transformed element
 */
export const findAndTransformStrict = <T, R>(array: T[], findTransform: (item: T) => R | undefined): R =>
	valOrThrow(findAndTransform(array, findTransform), 'No value found');

/**
 * Applies cb map function to the value if it is defined
 * @param value Value to map (possibly undefined)
 * @param cb Map/transform function
 * @returns Mapped value or undefined if the input value is undefined
 */
export const mapDefined = <TIn, TRes>(value: TIn | null | undefined, cb: (value: NonNullable<TIn>) => TRes) => {
	if (!isDefined(value)) return undefined;
	return cb(value as NonNullable<TIn>);
};

/**
 * Splits the given array into two arrays based on max length
 * @param arr Array to split
 * @param max Maximum length of the first array
 * @return Tuple of two arrays (first array with max length)
 */
export const splitMax = <T extends any>(arr: T[], max: number): [T[], T[]] => [arr.slice(0, max), arr.slice(max)];

/**
 * Moves an array item from one position in an array to another.
 * IMPORTANT: Confusing naming – This does not swap the 2 items,
 * it just inserts the item at the new position and shifts the other items.
 * Note: This is a pure function so a new array will be returned, instead of altering the array argument.
 * @param array An array where the item should be moved
 * @param moveIndex The index of the item to move
 * @param toIndex The index to move the item to
 * @returns A new array with the item moved to the new position, other items are in the same order just shifted.
 */
export function swap<T>(array: Array<T>, moveIndex: number, toIndex: number): Array<T> {
	const item = array[moveIndex]!;
	const { length } = array;
	const diff = moveIndex - toIndex;

	/** move left */
	if (diff > 0) {
		const targetIndex = toIndex;
		const start = array.slice(0, targetIndex);
		const middle = [item];
		const end = array.slice(targetIndex, moveIndex);
		const rest = array.slice(moveIndex + 1, length);
		return [...start, ...middle, ...end, ...rest];
	}

	/** move right */
	if (diff < 0) {
		const targetIndex = toIndex + 1;
		const start = array.slice(0, moveIndex);
		const rest = array.slice(moveIndex + 1, targetIndex);
		const middle = [item];
		const end = array.slice(targetIndex, length);
		return [...start, ...rest, ...middle, ...end];
	}

	/** no change */
	return array;
}

/**
 * Extracts all values from the given input with type of returnableType
 * @param {T} data
 * @param {V} returnableTypes
 * @returns {Array<V>}
 */
export const extractSameTypeFields = <T extends unknown, V extends unknown[] = unknown[]>(data: T, ...returnableTypes: V): V => {
	/** returnable input */
	if (returnableTypes.some((type) => typeof type === typeof data)) {
		return [data as V[number]] as V;
	}

	/** object */
	if (typeof data === 'object') {
		/** null is typeof object */
		if (!isDefined(data)) return [] as unknown as V;

		/** recursive array */
		if (Array.isArray(data)) {
			return data.reduce((acc, value) => acc.concat(...extractSameTypeFields(value, ...returnableTypes)), [] as unknown as V);
		}

		/** recursive object fields */
		const keys: (keyof T)[] = Object.keys(data as object) as (keyof T)[];
		return keys.reduce((acc: V, key) => acc.concat(...extractSameTypeFields((data as any)[key], ...returnableTypes)) as V, [] as unknown as V);
	}

	/** other invalid types */
	return [] as unknown as V;
};

/**
 * Cleans array from falsy values such as `undefined`, `null`, empty object or empty string
 * @param inputArray Array to clean
 * @returns Cleaned array without falsy or nullish values
 */
export const cleanArray = <T>(inputArray: T[]) =>
	inputArray.filter((value) => {
		if (isEmpty(value)) return false;
		if (isObject(value) && isEmptyObject(value)) return false;
		if (!value) return false;
		return true;
	});

/**
 * Return whether the given haystack contains any items from the given needles
 * @param {Array<T>} haystack
 * @param {T} needles
 * @returns {boolean}
 */
export const includesSome = <T>(haystack: Array<T> | undefined | null, ...needles: Array<T>): boolean =>
	Array.from(needles).some((p) => haystack?.includes(p));

/**
 * Determine if an array contains one or more items from another array
 * @deprecated use includesSome instead
 * @param {array} haystack the array to search.
 * @param {array} arr the array providing items to check for in the haystack.
 * @return {boolean} true|false if haystack contains at least one item from arr.
 */
export const hasSomeFromArray = (haystack: Array<any>, arr: Array<any>) => arr.some((v) => haystack.includes(v));

/**
 * Return whether the given haystack contains all items from the given needles
 * @param {Array<T>} haystack
 * @param {T} needles
 * @returns {boolean}
 */
export const includesAll = <T>(haystack: Array<T> | undefined | null, ...needles: Array<T>): boolean =>
	Array.from(needles).every((p) => haystack?.includes(p));

/**
 * Safely returns min/max values from array of numbers
 * @see https://stackoverflow.com/questions/42623071/maximum-call-stack-size-exceeded-with-math-min-and-math-max
 * @see https://nfctron.atlassian.net/browse/AC-1702
 * @param arr
 * @returns [number, number]
 */
export const arrayMinMax = (arr: number[]): [number, number] =>
	arr.reduce(([min, max], val) => [Math.min(min, val), Math.max(max, val)], [Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY]);

/**
 * Builds an array using the given builder function
 * @param builder Function to build the array
 * @returns Built array
 * @example
 * const arr = buildArray((current) => [...current, 1, 2, 3]);
 * console.log(arr); // [1, 2, 3]
 * @example
 * const arr = buildArray((current) => {
 *  if (x) current.push(1);
 *  if (y) current.push(2);
 *  return current;
 * })
 * console.log(arr); // [1] or [2] or [1, 2]
 */
export function buildArray<T>(builder: (current: T[]) => T[]): T[] {
	return builder([]);
}

/**
 * Shuffles an input array and returns a new array with the shuffled values
 * @param array Array of values to shuffle
 * @returns Shuffled array
 */
export function shuffleArray<T>(array: T[]): T[] {
	const shuffled = [...array];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		const shuffledI = shuffled[i]!;
		const shuffledJ = shuffled[j]!;
		shuffled[i] = shuffledJ;
		shuffled[j] = shuffledI;
	}
	return shuffled;
}
